import React, { useState } from 'react';
import { Table, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { useConfirm } from '../../shared/ConfirmContext';
import { IconPlus } from '@tabler/icons-react';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import AppButton from '../../components/ui/AppButton';

const ProjectTypes = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6'
  });

  const { data: projectTypes = [], isLoading } = useQuery({
    queryKey: ['project-types'],
    queryFn: () => api.get('projects/api/project-types/').then(d => Array.isArray(d) ? d : []),
  });

  const saveMutation = useMutation({
    mutationFn: (payload) => editingType
      ? api.put(`projects/api/project-types/${editingType.id}/`, payload)
      : api.post('projects/api/project-types/', payload),
    onSuccess: () => {
      setSuccess(editingType ? t('settings.projectTypes.success.updated') : t('settings.projectTypes.success.created'));
      setShowModal(false);
      queryClient.invalidateQueries({ queryKey: ['project-types'] });
    },
    onError: (err) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (typeId) => api.delete(`projects/api/project-types/${typeId}/`),
    onSuccess: () => {
      setSuccess(t('settings.projectTypes.success.deleted'));
      queryClient.invalidateQueries({ queryKey: ['project-types'] });
    },
    onError: (err) => setError(err.message),
  });

  const loading = isLoading || saveMutation.isPending || deleteMutation.isPending;

  const handleAdd = () => {
    setEditingType(null);
    setFormData({ name: '', description: '', color: '#3b82f6' });
    setShowModal(true);
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description || '',
      color: type.color || '#3b82f6'
    });
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    saveMutation.mutate(formData);
  };

  const handleDelete = async (typeId) => {
    if (!await confirm(t('settings.projectTypes.deleteConfirm'))) return;
    setError(null);
    setSuccess(null);
    deleteMutation.mutate(typeId);
  };

  return (
    <PageShell
      pretitle={t('settings.overview')}
      title={t('settings.projectTypes.title')}
      actions={
        <AppButton variant="primary" icon={<IconPlus size={16} />} onClick={handleAdd}>
          {t('settings.projectTypes.add')}
        </AppButton>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}
      {success && <div className="alert alert-success app-inline-alert" role="alert">{success}</div>}

      <PageSection
        title={t('settings.projectTypes.title')}
        description={t('settings.projectTypes.subtitle')}
        bodyClassName="p-0"
      >
                    {loading && projectTypes.length === 0 ? (
                      <div className="text-center p-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        {t('common.loading')}
                      </div>
                    ) : (
                      <Table responsive hover className="mb-0">
                        <thead>
                          <tr>
                            <th>{t('settings.projectTypes.name')}</th>
                            <th>{t('settings.projectTypes.color')}</th>
                            <th>{t('settings.projectTypes.description')}</th>
                            <th className="text-end">{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectTypes.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-center text-muted py-4">
                                {t('settings.projectTypes.noTypes')}
                              </td>
                            </tr>
                          ) : projectTypes.map(type => (
                            <tr key={type.id}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      width: 14,
                                      height: 14,
                                      borderRadius: 3,
                                      backgroundColor: type.color || '#ccc',
                                      flexShrink: 0
                                    }}
                                  />
                                  <strong>{type.name}</strong>
                                </div>
                              </td>
                              <td>
                                <code>{type.color}</code>
                              </td>
                              <td className="text-muted">{type.description || '—'}</td>
                              <td className="text-end">
                                <div className="btn-group">
                                  <Button
                                    size="sm"
                                    variant="outline-info"
                                    onClick={() => handleEdit(type)}
                                    disabled={loading}
                                    title={t('common.edit')}
                                  >
                                    <i className="ti ti-edit"></i>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDelete(type.id)}
                                    disabled={loading}
                                    title={t('common.delete')}
                                  >
                                    <i className="ti ti-trash"></i>
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
      </PageSection>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingType ? t('settings.projectTypes.edit') : t('settings.projectTypes.add')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.projectTypes.name')} *</Form.Label>
              <Form.Control
                type="text"
                required
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('settings.projectTypes.namePlaceholder')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.projectTypes.color')}</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="color"
                  value={formData.color}
                  onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  style={{ width: 48, height: 38, padding: 2, cursor: 'pointer' }}
                />
                <Form.Control
                  type="text"
                  value={formData.color}
                  onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="#3b82f6"
                  pattern="^#[0-9A-Fa-f]{6}$"
                />
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.projectTypes.description')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('settings.projectTypes.descriptionPlaceholder')}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              {t('common.cancel')}
            </Button>
            <AppButton type="submit" variant="primary" loading={loading} loadingText={t('common.saving')}>
              {editingType ? t('common.save') : t('settings.projectTypes.add')}
            </AppButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </PageShell>
  );
};

export default ProjectTypes;
